import {
  STRATEGY_TAG_TYPE,
  ALL_STRATEGIES_ID,
  USER_STRATEGIES_ID,
  TERRITORIES_ID,
  ENGAGE_MAP_MEMBERS_ID,
  ENGAGE_LIST_MEMBERS_ID,
} from './constants';
import { strategyApi } from './strategyApi';
import { buildSearchParamsAuto } from '../utils';
import type {
  EngageStrategyDetail,
  StrategyMapMember,
  StrategyListMember,
  StrategyHistoryEntry,
  EngageMapParams,
} from './types';

interface EngageStrategiesParams {
  q?: string;
  [key: string]: any;
}

export const engageStrategyApi = strategyApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllEngageStrategies: builder.query<Record<string, any>, EngageStrategiesParams | void>({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        const queryString = queryParams?.toString();
        return `v1/strategies/engage-strategies/?${queryString}`;
      },
      providesTags: (result) => {
        const strategies = Array.isArray(result) ? result : [];
        return [
          {
            type: STRATEGY_TAG_TYPE,
            id: ALL_STRATEGIES_ID,
          },
          ...strategies.map(({ id }: any) => ({
            type: STRATEGY_TAG_TYPE,
            id,
          })),
        ];
      },
    }),
    getEngageStrategyDetails: builder.query<EngageStrategyDetail, string>({
      query: (id) => `v1/strategies/engage-strategies/${id}/`,
      providesTags: (result, error, id) => [{ type: STRATEGY_TAG_TYPE, id }],
    }),
    createEngageStrategy: builder.mutation<EngageStrategyDetail, Record<string, any>>({
      query: (strategy) => ({
        url: 'v1/strategies/engage-strategies/',
        method: 'POST',
        body: strategy,
      }),
      invalidatesTags: [
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: TERRITORIES_ID,
        },
      ],
    }),
    updateEngageStrategy: builder.mutation<EngageStrategyDetail, Record<string, any> & { id: string | number }>({
      query: (strategy) => ({
        url: `v1/strategies/engage-strategies/${strategy?.id}/`,
        method: 'PATCH',
        body: strategy,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: STRATEGY_TAG_TYPE, id },
        { type: STRATEGY_TAG_TYPE, id: ALL_STRATEGIES_ID },
        { type: STRATEGY_TAG_TYPE, id: USER_STRATEGIES_ID },
        { type: STRATEGY_TAG_TYPE, id: TERRITORIES_ID },
      ],
    }),
    deleteEngageStrategy: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/strategies/engage-strategies/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: STRATEGY_TAG_TYPE, id },
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
        // Invalidate all chained strategies since they may reference this engage strategy
        {
          type: STRATEGY_TAG_TYPE,
          id: 'MY_CHAINED_STRATEGIES',
        },
      ],
    }),
    runEngageStrategy: builder.mutation<
      void,
      { strategyId: string; providerIds: string[] }
    >({
      query: ({ strategyId, providerIds }) => ({
        url: `v1/strategies/engage-strategies/${strategyId}/`,
        method: 'POST',
        body: { provider_ids: providerIds },
      }),
      invalidatesTags: (result, error, { strategyId, providerIds }) => [
        { type: STRATEGY_TAG_TYPE, id: strategyId },
        ...providerIds.map((pid) => ({
          type: STRATEGY_TAG_TYPE,
          id: `CONNECTOR_${strategyId}_${pid}`,
        })),
      ],
    }),
    getEngageStrategyListMembers: builder.query<MedScout.PaginatedResponse & { results: StrategyListMember[] }, EngageMapParams>({
      query: (params) => {
        const { strategyId, provider_id, bounds, ...queryParams } = params;

        const searchParams = buildSearchParamsAuto(
          {
            ...queryParams,
            ...(bounds && {
              min_lat: bounds.south,
              max_lat: bounds.north,
              min_lng: bounds.west,
              max_lng: bounds.east,
            }),
          },
          {
            cities: 'city',
            states: 'state',
            specialties: 'specialty',
            statuses: 'crm_status',
            provider_lists: 'provider_list',
          }
        );

        return {
          url: `v1/strategies/engage-strategies/${strategyId}/providers/${provider_id}/members/?${searchParams.toString()}`,
        };
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: ENGAGE_LIST_MEMBERS_ID }],
    }),
    getEngageStrategyListMembersPaginated: builder.infiniteQuery<
      {
        results: any[];
        count?: number;
        next?: string | null;
        previous?: string | null;
      },
      {
        strategyId: string;
        provider_id: string;
        page_size?: number;
        bounds?: any;
        [key: string]: any;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
        getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
          firstPageParam > 1 ? firstPageParam - 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const { strategyId, provider_id, bounds, page_size, ...restParams } =
          queryArg;

        const searchParams = buildSearchParamsAuto(
          {
            ...restParams,
            page: pageParam,
            page_size: page_size || 25,
            ...(bounds && {
              min_lat: bounds.south,
              max_lat: bounds.north,
              min_lng: bounds.west,
              max_lng: bounds.east,
            }),
          },
          {
            cities: 'city',
            states: 'state',
            specialties: 'specialty',
            statuses: 'crm_status',
            provider_lists: 'provider_list',
          }
        );

        return {
          url: `v1/strategies/engage-strategies/${strategyId}/providers/${provider_id}/members/?${searchParams.toString()}`,
        };
      },
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: ENGAGE_LIST_MEMBERS_ID }],
    }),
    getEngageMapMembers: builder.query<StrategyMapMember[], EngageMapParams>({
      query: (params) => {
        const { strategyId, provider_id, bounds, ...queryParams } = params;

        const searchParams = buildSearchParamsAuto(
          {
            ...queryParams,
            ...(bounds && {
              min_lat: bounds.south,
              max_lat: bounds.north,
              min_lng: bounds.west,
              max_lng: bounds.east,
            }),
          },
          {
            cities: 'city',
            states: 'state',
            specialties: 'specialty',
            statuses: 'crm_status',
            provider_lists: 'provider_list',
          }
        );

        return {
          url: `v1/strategies/engage-strategies/${strategyId}/providers/${provider_id}/map/?${searchParams.toString()}`,
        };
      },
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: ENGAGE_MAP_MEMBERS_ID }],
    }),
    // Connectors
    getEngageStrategyConnector: builder.query<
      {
        id: string | number;
        provider_id: string;
        provider__name: string;
        status: string;
        list_items_count: number;
        last_refreshed: string | null;
        metrics: Record<string, Record<string, number>>;
      },
      { strategyId: string; providerId: string }
    >({
      query: ({ strategyId, providerId }) =>
        `v1/strategies/engage-strategies/${strategyId}/providers/${providerId}/connector/`,
      providesTags: (result, error, { strategyId, providerId }) => [
        {
          type: STRATEGY_TAG_TYPE,
          id: `CONNECTOR_${strategyId}_${providerId}`,
        },
      ],
    }),
    getEngageStrategyConnectors: builder.infiniteQuery<
      {
        results: Array<{
          id: string | number;
          status: string;
          provider_name: string;
          list_items_count: number;
          provider_id: string;
          provider_type: string;
          provider__name: string;
        }>;
        count?: number;
        next?: string | null;
        previous?: string | null;
      },
      { strategyId: string | null; params?: any },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
        getPreviousPageParam: (firstPage, allPages, firstPageParam) =>
          firstPageParam > 1 ? firstPageParam - 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const { page_size, ...restParams } = queryArg.params || {};
        const searchParams = buildSearchParamsAuto({
          ...restParams,
          page: pageParam,
          page_size: page_size || 50,
        });
        return `v1/strategies/engage-strategies/${
          queryArg.strategyId
        }/connectors/?${searchParams.toString()}`;
      },
    }),
    getEngageStrategyHeatmap: builder.query<
      {
        points: Array<{
          lat: number;
          lng: number;
          volume: number;
        }>;
      },
      { strategyId: string; providerId: string; params?: { metric?: string } }
    >({
      query: ({ strategyId, providerId, params }) => {
        const searchParams = buildSearchParamsAuto(params || {});
        const queryString = searchParams?.toString();
        return `v1/strategies/engage-strategies/${strategyId}/providers/${providerId}/heat-map/?${queryString}`;
      },
    }),
    // History
    getEngageStrategyHistory: builder.query<MedScout.PaginatedResponse & { results: StrategyHistoryEntry[] }, { strategyId: string }>({
      query: ({ strategyId }) =>
        `v1/strategies/engage-strategies/${strategyId}/history/`,
    }),
  }),
});

export const {
  useGetAllEngageStrategiesQuery,
  useGetEngageStrategyDetailsQuery,
  useCreateEngageStrategyMutation,
  useRunEngageStrategyMutation,
  useGetEngageStrategyListMembersQuery,
  useGetEngageMapMembersQuery,
  useUpdateEngageStrategyMutation,
  useDeleteEngageStrategyMutation,
  useGetEngageStrategyListMembersPaginatedInfiniteQuery,
  useGetEngageStrategyConnectorsInfiniteQuery,
  useGetEngageStrategyConnectorQuery,
  useGetEngageStrategyHeatmapQuery,
  useGetEngageStrategyHistoryQuery,
} = engageStrategyApi;
